import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Pagination from 'components/@extended/Pagination';

// material-ui
import { Box, IconButton, Link, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

// third-party
import NumberFormat from 'react-number-format';

// project import
import Dot from 'components/@extended/Dot';
import { getRequest } from 'utils/apiRequest';
import config from 'config';
import MainCard from 'components/MainCard';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { hasFlag } from 'country-flag-icons'
import Flags from "country-flag-icons/react/3x2";
import { getUrlForPath } from 'utils/functions';

function createData(deviceId, zipCode, city, stateCode, countryCode, ipAddress, isGuess, createdAt) {
    return { deviceId, zipCode, city, stateCode, countryCode, ipAddress, isGuess, createdAt };
}


function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

// ==============================|| ORDER TABLE - HEADER CELL ||============================== //

const headCells = [
    {
        id: 'user',
        align: 'left',
        disablePadding: false,
        label: 'User'
    },
    {
        id: 'zipCode',
        align: 'left',
        disablePadding: true,
        label: 'Zip Code'
    },
    {
        id: 'city',
        align: 'left',
        disablePadding: true,
        label: 'City'
    },
    {
        id: 'stateCode',
        align: 'left',
        disablePadding: true,
        label: 'State'
    },
    // {
    //     id: 'ipAddress',
    //     align: 'center',
    //     disablePadding: false,
    //     label: 'IP Address'
    // },
    {
        id: 'isGuess',
        align: 'center',
        disablePadding: false,

        label: 'Confirmed'
    },
    {
        id: 'createdAt',
        align: 'left',
        disablePadding: true,
        label: 'DateTime'
    },
];

// ==============================|| ORDER TABLE - HEADER ||============================== //

function RecentZipCodesTableHead({ order, orderBy }) {
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.align}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {headCell.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

RecentZipCodesTableHead.propTypes = {
    order: PropTypes.string,
    orderBy: PropTypes.string
};

// ==============================|| ORDER TABLE - STATUS ||============================== //

const OrderStatus = ({ status }) => {
    let color;
    let title;

    switch (status) {
        case 1:
            color = 'error';
            title = 'Guess';
            break;
        case 0:
            color = 'success';
            title = 'Confirmed';
            break;
        default:
            color = 'primary';
            title = 'None';
    }

    return (<>
        {/*<Box display="flex" alignItems="center" align="right">
            <Typography sx={{mr:1}}>{title}</Typography>*/}
        <Dot color={color} />
        {/* </Box> */}
    </>
    );
};

OrderStatus.propTypes = {
    status: PropTypes.number
};

// ==============================|| ORDER TABLE ||============================== //

export default function RecentZipCodesTable() {
    const [order] = useState('asc');
    const [orderBy] = useState('trackingNo');
    const [selected] = useState([]);
    const [recentZips, setRecentZips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 1,
    });

    useEffect(() => {
        getRecentZips()
    }, []);

    const setPage = (p) => {
        getRecentZips(p);
    };
    const getRecentZips = (p = 1) => {
        setLoading(true);
        getRequest('/recent/zips?page=' + p).then((data) => {
            const rows = [];
            if(data && data.data){
                data.data.forEach((d) => {
                    if(d.zipcode && d.zipcode.city){
                        rows.push(createData(d.device_id, d.zip_code, d.zipcode?.city, d.zipcode?.state_code, d.zipcode?.country_code, d.ip_address, d.is_guess, d.created_at));
                    }
                })
                setRecentZips(rows);
                setPagination({
                    current: data.current_page,
                    total: data.last_page,
                })
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    const FlagByCode = ({code}) => {
        const Flag = Flags[hasFlag(code) ? code : 'US'];   
        return <Flag style={{
            width: 18, 
            position: 'absolute',
            bottom: 2,
            left: 10
        }} />
    }

    const isSelected = (trackingNo) => selected.indexOf(trackingNo) !== -1;

    return (!loading && recentZips.length === 0 ? <></> : (
        <MainCard title={"Recent Zip Codes"}>
        <Box>
            <TableContainer
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                    position: 'relative',
                    display: 'block',
                    maxWidth: '100%',
                    '& td, & th': { whiteSpace: 'nowrap' }
                }}
            >
                <Table
                    aria-labelledby="tableTitle"
                    sx={{
                        '& .MuiTableCell-root:first-of-type': {
                            pl: 2
                        },
                        '& .MuiTableCell-root:last-child': {
                            pr: 3
                        }
                    }}
                >
                    <RecentZipCodesTableHead order={order} orderBy={orderBy} />
                    <TableBody>
                        {loading ? ([...Array(10)].map((k, i) => {
                        return (<TableRow key={i}>
                            <TableCell align="left">
                                <Skeleton variant="text" width="70%" />
                            </TableCell>
                            <TableCell align="left">
                                <Skeleton variant="rounded" width="70%" />
                            </TableCell>
                            <TableCell align="left">
                                <Skeleton variant="text" width="70%" />
                            </TableCell>
                            <TableCell align="left">
                                <Skeleton variant="text" width="70%" />
                            </TableCell>
                            <TableCell align="left">
                                <Skeleton variant="text" width="70%" />
                            </TableCell>
                            <TableCell align="center">
                                <Skeleton variant="text" width="70%" />
                            </TableCell>
                            <TableCell align="center">
                                <Skeleton variant="circle" width="70%" />
                            </TableCell>
                        </TableRow>)
                        })
                        ) : (<>
                        {stableSort(recentZips, getComparator(order, orderBy)).map((row, index) => {
                            const isItemSelected = isSelected(row.trackingNo);
                            const labelId = `enhanced-table-checkbox-${index}`;
                            return (
                                <TableRow
                                    hover
                                    role="checkbox"
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    aria-checked={isItemSelected}
                                    tabIndex={-1}
                                    key={index}
                                    selected={isItemSelected}
                                >
                                    <TableCell component="th" id={labelId} scope="row" align="left">
                                        <Link color="secondary" component={RouterLink} to="" sx={{position: 'relative'}}>
                                            <AccountCircleIcon />
                                            <FlagByCode code={row.countryCode} />
                                        </Link>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Link color="secondary" href={getUrlForPath("/maps/recent/" + row.zipCode)}>
                                            {row.zipCode}
                                        </Link>
                                    </TableCell>
                                    <TableCell align="left">{row.city}</TableCell>
                                    <TableCell align="left">{row.stateCode}</TableCell>
                                    {/* <TableCell align="center">{row.ipAddress}</TableCell> */}
                                    <TableCell align="center">
                                        <OrderStatus status={row.isGuess} />
                                    </TableCell>
                                    <TableCell id={labelId} align="left">
                                        {(new Date(row.createdAt)).toLocaleString()}
                                    </TableCell>
                                </TableRow>
                            );
                        }
                        )}</>)}
                    </TableBody>
                </Table>
            </TableContainer>
            <Pagination
                count={pagination.total}
                page={pagination.current}
                setPage={(p) => setPage(p)}
            />
        </Box>
        </MainCard>
    )
    );
}
